// BKLS public + customer server functions. Client-safe module path.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { publicClient } from "@/lib/shop.server";
import { checkRateLimit } from "@/lib/rate-limit.server";
import { validateEmail } from "@/lib/email-validation";
import { CustomRequestInput } from "@/lib/bkls-custom-request";
import type { CustomRequestOutcome } from "@/lib/bkls-custom-request";


import type { BklsSettings, IssuedLicense, LicenseTier, PublicBeat } from "@/lib/bkls";

const SlugSchema = z.string().trim().min(1).max(200).regex(/^[a-z0-9-]+$/);

const BEAT_PUBLIC_COLUMNS =
  "id, slug, title, subtitle, description, bpm, musical_key, genre, mood, tags, duration_seconds, artwork_path, preview_path, sort_order, created_at";

// ---------------- Settings / feature flag ----------------

export const getBklsSettings = createServerFn({ method: "GET" }).handler(
  async (): Promise<BklsSettings> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { loadSettings } = await import("@/lib/bkls.server");
    return loadSettings(supabaseAdmin);
  },
);

// ---------------- Catalog ----------------

export const listLicenseTiers = createServerFn({ method: "GET" }).handler(
  async (): Promise<LicenseTier[]> => {
    const supabase = publicClient();
    const { data, error } = await (supabase.from("license_tiers") as any)
      .select(
        "key, name, tagline, price_cents, currency, file_formats, stream_limit, video_limit, distribution_limit, monetization, performance_rights, territory, term, credit_required, restrictions, is_quote_only, active, sort_order",
      )
      .eq("active", true)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as LicenseTier[];
  },
);

export const listBeats = createServerFn({ method: "GET" }).handler(async (): Promise<PublicBeat[]> => {
  const supabase = publicClient();
  const { data, error } = await (supabase.from("beats") as any)
    .select(BEAT_PUBLIC_COLUMNS)
    .eq("status", "published")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as any[];
  if (!rows.length) return [];

  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { signPath, ARTWORK_BUCKET, PREVIEW_TTL_SECONDS } = await import("@/lib/bkls.server");
  const { data: prices } = await (supabase.from("beat_tier_prices") as any)
    .select("beat_id, tier_key, price_cents, available")
    .in("beat_id", rows.map((r) => r.id));

  return Promise.all(
    rows.map(async (row) => ({
      id: row.id,
      slug: row.slug,
      title: row.title,
      subtitle: row.subtitle,
      description: row.description,
      bpm: row.bpm,
      musical_key: row.musical_key,
      genre: row.genre,
      mood: row.mood,
      tags: row.tags ?? [],
      duration_seconds: row.duration_seconds,
      artworkUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.artwork_path, PREVIEW_TTL_SECONDS),
      previewUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.preview_path, PREVIEW_TTL_SECONDS),
      prices: (prices ?? []).filter((p: any) => p.beat_id === row.id),
    })),
  );
});

export const getBeat = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: SlugSchema }).parse(input))
  .handler(async ({ data }): Promise<PublicBeat | null> => {
    const supabase = publicClient();
    const { data: row, error } = await (supabase.from("beats") as any)
      .select(BEAT_PUBLIC_COLUMNS)
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { signPath, ARTWORK_BUCKET, PREVIEW_TTL_SECONDS } = await import("@/lib/bkls.server");
    const { data: prices } = await (supabase.from("beat_tier_prices") as any)
      .select("tier_key, price_cents, available")
      .eq("beat_id", row.id);

    return {
      id: row.id,
      slug: row.slug,
      title: row.title,
      subtitle: row.subtitle,
      description: row.description,
      bpm: row.bpm,
      musical_key: row.musical_key,
      genre: row.genre,
      mood: row.mood,
      tags: row.tags ?? [],
      duration_seconds: row.duration_seconds,
      artworkUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.artwork_path, PREVIEW_TTL_SECONDS),
      previewUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.preview_path, PREVIEW_TTL_SECONDS),
      prices: prices ?? [],
    };
  });

export const getLicenseTemplates = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await (supabase.from("license_templates") as any)
    .select("tier_key, version, title, body_md, effective_from")
    .eq("active", true)
    .order("tier_key", { ascending: true })
    .order("version", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<{
    tier_key: string;
    version: number;
    title: string;
    body_md: string;
    effective_from: string;
  }>;
});

// ---------------- Custom licence requests ----------------

export const submitCustomLicenseRequest = createServerFn({ method: "POST" })
  .inputValidator((input) => CustomRequestInput.parse(input))
  .handler(async ({ data }): Promise<CustomRequestOutcome | { error: string }> => {
    const allowed = await checkRateLimit(`bkls-custom:${data.contactEmail.toLowerCase()}`, 5, 3600);
    if (!allowed) return { error: "Too many requests. Please try again later." };
    const { persistAndNotifyCustomRequest } = await import("@/lib/bkls-notify.server");
    return persistAndNotifyCustomRequest(data, null);
  });


/** Signed-in variant: attaches the request to the account so it appears in history. */
export const submitMyCustomLicenseRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CustomRequestInput.parse(input))
  .handler(async ({ data, context }): Promise<CustomRequestOutcome | { error: string }> => {
    const allowed = await checkRateLimit(`bkls-custom:${context.userId}`, 10, 3600);
    if (!allowed) return { error: "Too many requests. Please try again later." };
    const { persistAndNotifyCustomRequest } = await import("@/lib/bkls-notify.server");
    return persistAndNotifyCustomRequest(data, context.userId);

  });


// ---------------- Customer account ----------------

export const getMyBeatAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(
    async ({
      context,
    }): Promise<{ licenses: IssuedLicense[]; requests: any[]; downloads: any[] }> => {
      const [lic, req, dl] = await Promise.all([
        context.supabase
          .from("licenses")
          .select(
            "id, license_number, beat_slug, beat_title, tier_key, tier_name, price_cents, currency, status, issued_at, template_version, terms_snapshot, agreement_md, licensee_name",
          )
          .eq("user_id", context.userId)
          .order("issued_at", { ascending: false }),
        context.supabase
          .from("custom_license_requests")
          .select("id, beat_slug, intended_use, status, created_at, budget_cents, timeline")
          .eq("user_id", context.userId)
          .order("created_at", { ascending: false }),
        context.supabase
          .from("beat_downloads")
          .select("id, beat_id, storage_path, created_at, status")
          .eq("user_id", context.userId)
          .order("created_at", { ascending: false })
          .limit(50),
      ]);
      return {
        licenses: (lic.data ?? []) as unknown as IssuedLicense[],
        requests: req.data ?? [],
        downloads: dl.data ?? [],
      };
    },
  );

/** Signed, short-lived download links for the files a licence actually covers. */
export const requestLicensedFiles = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ licenseId: z.string().uuid() }).parse(input))
  .handler(
    async ({
      data,
      context,
    }): Promise<{ files: Array<{ label: string; kind: string; url: string; expiresInSeconds: number }> } | { error: string }> => {
      const allowed = await checkRateLimit(`bkls-download:${context.userId}`, 30, 3600);
      if (!allowed) return { error: "Too many download requests. Please try again shortly." };

      const { data: license } = await context.supabase
        .from("licenses")
        .select("id, user_id, beat_id, status, terms_snapshot")
        .eq("id", data.licenseId)
        .eq("user_id", context.userId)
        .maybeSingle();
      if (!license) return { error: "Licence not found." };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { resolveLicensedFiles } = await import("@/lib/bkls.server");
      return resolveLicensedFiles(supabaseAdmin, license as any);
    },
  );


// ---------------- Checkout (disabled until launch-approved) ----------------

export const createBeatCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        slug: SlugSchema,
        tierKey: z.string().trim().min(1).max(60),
        returnUrl: z.string().url(),
        consent: z.object({ terms: z.literal(true), license: z.literal(true) }),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<{ clientSecret: string } | { error: string }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { loadSettings } = await import("@/lib/bkls.server");
    const settings = await loadSettings(supabaseAdmin);

    if (!settings.checkout_enabled) {
      return {
        error:
          "Beat licence checkout is not open yet. Send a custom licensing request and we will respond directly.",
      };
    }
    if (settings.checkout_mode === "live" && !settings.legal_reviewed) {
      return { error: "Live licence sales are blocked until the legal review is recorded." };
    }

    const [{ data: beat }, { data: tier }] = await Promise.all([
      (supabaseAdmin.from("beats") as any).select("id, slug, title, status").eq("slug", data.slug).maybeSingle(),
      (supabaseAdmin.from("license_tiers") as any).select("*").eq("key", data.tierKey).eq("active", true).maybeSingle(),
    ]);
    if (!beat || beat.status !== "published") return { error: "This beat is not available." };
    if (!tier) return { error: "That licence tier is not available." };
    if (tier.is_quote_only) return { error: "This tier is quote-only. Please submit a custom request." };

    const { data: override } = await (supabaseAdmin.from("beat_tier_prices") as any)
      .select("price_cents, available")
      .eq("beat_id", beat.id)
      .eq("tier_key", tier.key)
      .maybeSingle();
    if (override && override.available === false) return { error: "That licence tier is not offered for this beat." };
    const priceCents = override?.price_cents ?? tier.price_cents;
    if (!priceCents || priceCents < 50) return { error: "This licence price is not configured yet." };

    try {
      const { createStripeClient, getStripeErrorMessage } = await import("@/lib/stripe.server");
      const { resolveOrCreateCustomer } = await import("@/lib/shop.server");
      const env = settings.checkout_mode;
      const stripe = createStripeClient(env);
      const email = (context.claims as { email?: string } | undefined)?.email;
      const customerId = await resolveOrCreateCustomer(stripe, { email, userId: context.userId });

      try {
        const session = await stripe.checkout.sessions.create({
          mode: "payment",
          ui_mode: "embedded_page",
          customer: customerId,
          return_url: data.returnUrl,
          line_items: [
            {
              quantity: 1,
              price_data: {
                currency: tier.currency ?? "usd",
                unit_amount: priceCents,
                product_data: { name: `${beat.title} — ${tier.name}` },
              },
            },
          ],
          metadata: {
            userId: context.userId,
            product_slug: `beat-${beat.slug}-${tier.key}`,
            product_title: `${beat.title} — ${tier.name}`,
            bkls_beat_slug: beat.slug,
            bkls_tier_key: tier.key,
            consent_terms_at: new Date().toISOString(),
            consent_license_at: new Date().toISOString(),
          },
        });
        if (!session.client_secret) return { error: "Could not start checkout." };
        return { clientSecret: session.client_secret };
      } catch (err) {
        return { error: getStripeErrorMessage(err) };
      }
    } catch {
      return { error: "Checkout is not configured." };
    }
  });

// ---------------- Collections (public) ----------------

export type PublicCollection = {
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  seo_title: string | null;
  seo_description: string | null;
  beatCount: number;
};

/** Active collections, annotated with how many published beats they hold. */
export const listBeatCollections = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicCollection[]> => {
    const supabase = publicClient();
    const [{ data: collections }, { data: beats }] = await Promise.all([
      (supabase.from("beat_collections") as any)
        .select("slug, name, tagline, description, seo_title, seo_description, sort_order")
        .eq("active", true)
        .order("sort_order", { ascending: true }),
      (supabase.from("beats") as any).select("collection_slug").eq("status", "published"),
    ]);
    const counts = new Map<string, number>();
    for (const b of (beats ?? []) as any[]) {
      if (!b.collection_slug) continue;
      counts.set(b.collection_slug, (counts.get(b.collection_slug) ?? 0) + 1);
    }
    return ((collections ?? []) as any[]).map((c) => ({
      slug: c.slug,
      name: c.name,
      tagline: c.tagline,
      description: c.description,
      seo_title: c.seo_title,
      seo_description: c.seo_description,
      beatCount: counts.get(c.slug) ?? 0,
    }));
  },
);

export const getBeatCollection = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: SlugSchema }).parse(input))
  .handler(async ({ data }): Promise<{ collection: PublicCollection; beats: PublicBeat[] } | null> => {
    const supabase = publicClient();
    const { data: collection } = await (supabase.from("beat_collections") as any)
      .select("slug, name, tagline, description, seo_title, seo_description")
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (!collection) return null;

    const { data: rows } = await (supabase.from("beats") as any)
      .select(BEAT_PUBLIC_COLUMNS)
      .eq("status", "published")
      .eq("collection_slug", data.slug)
      .order("sort_order", { ascending: true });

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { signPath, ARTWORK_BUCKET, PREVIEW_TTL_SECONDS } = await import("@/lib/bkls.server");
    const beats = await Promise.all(
      ((rows ?? []) as any[]).map(async (row) => ({
        id: row.id,
        slug: row.slug,
        title: row.title,
        subtitle: row.subtitle,
        description: row.description,
        bpm: row.bpm,
        musical_key: row.musical_key,
        genre: row.genre,
        mood: row.mood,
        tags: row.tags ?? [],
        duration_seconds: row.duration_seconds,
        artworkUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.artwork_path, PREVIEW_TTL_SECONDS),
        previewUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.preview_path, PREVIEW_TTL_SECONDS),
        prices: [],
      })),
    );
    return {
      collection: { ...(collection as any), beatCount: beats.length },
      beats: beats as unknown as PublicBeat[],
    };
  });

// ---------------- Analytics (real interactions only) ----------------

/**
 * Records a public interaction. Purchase events are never accepted here —
 * those are written only by the trusted fulfilment path.
 */
export const recordBeatEvent = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        beatSlug: SlugSchema,
        eventType: z.enum(["view", "preview_play", "preview_complete", "license_click", "checkout_start"]),
        tierKey: z.string().trim().max(60).optional().or(z.literal("")),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<{ ok: boolean }> => {
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const ip = getRequestHeader("cf-connecting-ip") ?? getRequestHeader("x-forwarded-for") ?? "unknown";
    const allowed = await checkRateLimit(`bkls-event:${ip}`, 300, 300);
    if (!allowed) return { ok: false };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: beat } = await (supabaseAdmin.from("beats") as any)
      .select("id, status")
      .eq("slug", data.beatSlug)
      .maybeSingle();
    if (!beat || beat.status !== "published") return { ok: false };

    // Coarse, rotating, non-reversible visitor hash — no IP or UA is stored.
    const day = new Date().toISOString().slice(0, 10);
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(`${ip}|${getRequestHeader("user-agent") ?? ""}|${day}`),
    );
    const visitorHash = Array.from(new Uint8Array(digest).slice(0, 12))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    let referrerHost: string | null = null;
    const referer = getRequestHeader("referer");
    if (referer) {
      try {
        referrerHost = new URL(referer).hostname;
      } catch {
        referrerHost = null;
      }
    }

    await (supabaseAdmin.from("beat_analytics_events") as any).insert({
      beat_id: beat.id,
      beat_slug: data.beatSlug,
      event_type: data.eventType,
      tier_key: data.tierKey || null,
      visitor_hash: visitorHash,
      country: getRequestHeader("cf-ipcountry") ?? null,
      referrer_host: referrerHost,
    });
    return { ok: true };
  });

// ---------------- Favourites / wishlist ----------------

/**
 * Resolve slugs against the PUBLIC beat catalog.
 *
 * Gates on both `status` and `workflow_status` so that only genuinely
 * published work resolves. The `bkls_workflow_guard` trigger keeps the two
 * columns in sync, so this is belt-and-braces — it matches how
 * `search_temple` and the rest of the public surface gate, and it means a
 * future direct write to one column alone cannot expose unreleased beats.
 *
 * Returns a slug -> public title map. A missing key means "not public",
 * without distinguishing draft, archived, retired or nonexistent.
 */
async function publishedBeatTitles(slugs: string[]): Promise<Map<string, string>> {
  const unique = Array.from(new Set(slugs));
  if (!unique.length) return new Map();
  const supabase = publicClient();
  const { data } = await (supabase.from("beats") as any)
    .select("slug, title")
    .eq("status", "published")
    .eq("workflow_status", "published")
    .in("slug", unique);
  return new Map<string, string>((data ?? []).map((b: any) => [b.slug, b.title]));
}

export const getMyBeatFavorites = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ favorites: string[]; wishlist: string[] }> => {
    const { data } = await context.supabase
      .from("user_favorites")
      .select("kind, slug")
      .eq("user_id", context.userId)
      .in("kind", ["beat", "beat_wishlist"]);
    const rows = (data ?? []) as Array<{ kind: string; slug: string }>;
    return {
      favorites: rows.filter((r) => r.kind === "beat").map((r) => r.slug),
      wishlist: rows.filter((r) => r.kind === "beat_wishlist").map((r) => r.slug),
    };
  });

export const toggleBeatFavorite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ slug: SlugSchema, kind: z.enum(["beat", "beat_wishlist"]) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<{ saved: boolean; unavailable?: boolean }> => {
    const { data: existing } = await context.supabase
      .from("user_favorites")
      .select("id")
      .eq("user_id", context.userId)
      .eq("kind", data.kind)
      .eq("slug", data.slug)
      .maybeSingle();
    if (existing) {
      // Removal always succeeds, even for a beat that has since been pulled,
      // so a stale save can never become unremovable.
      await context.supabase.from("user_favorites").delete().eq("id", (existing as any).id);
      return { saved: false };
    }
    // Only publicly published beats can be saved. This keeps arbitrary or
    // unreleased slugs out of the list entirely. It reveals nothing new:
    // "published" is already public, and every other state is reported
    // identically as unavailable.
    const titles = await publishedBeatTitles([data.slug]);
    if (!titles.has(data.slug)) return { saved: false, unavailable: true };

    await context.supabase
      .from("user_favorites")
      .insert({ user_id: context.userId, kind: data.kind, slug: data.slug });
    return { saved: true };
  });


export interface SavedBeatItem {
  slug: string;
  kind: "beat" | "beat_wishlist";
  /** Public catalog title. Null whenever the beat is not publicly published. */
  title: string | null;
  /** True only when the beat is live in the public catalog and safe to link to. */
  available: boolean;
}

/**
 * RLS-scoped read of the signed-in user's saved beats, resolved against the
 * PUBLIC catalog only.
 *
 * Anything that is not publicly published — draft, in review, scheduled,
 * archived, retired, or deleted outright — resolves to `available: false`
 * with a null title. No workflow state, review note, or other internal
 * field is ever returned, so an unavailable save cannot be used to infer
 * anything about unreleased work beyond "not currently public".
 */
export const getMyBeatSaves = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ favorites: SavedBeatItem[]; wishlist: SavedBeatItem[] }> => {
    const { data } = await context.supabase
      .from("user_favorites")
      .select("kind, slug")
      .eq("user_id", context.userId)
      .in("kind", ["beat", "beat_wishlist"]);
    const rows = (data ?? []) as Array<{ kind: "beat" | "beat_wishlist"; slug: string }>;
    if (!rows.length) return { favorites: [], wishlist: [] };

    const titles = await publishedBeatTitles(rows.map((r) => r.slug));

    const items = rows.map((r) => ({
      slug: r.slug,
      kind: r.kind,
      title: titles.get(r.slug) ?? null,
      available: titles.has(r.slug),
    }));
    return {
      favorites: items.filter((i) => i.kind === "beat"),
      wishlist: items.filter((i) => i.kind === "beat_wishlist"),
    };
  });


export const removeBeatSave = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ slug: SlugSchema, kind: z.enum(["beat", "beat_wishlist"]) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<{ removed: boolean }> => {
    const { error } = await context.supabase
      .from("user_favorites")
      .delete()
      .eq("user_id", context.userId)
      .eq("kind", data.kind)
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { removed: true };
  });
